import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import SignalWifiOffIcon from '@mui/icons-material/SignalWifi1Bar';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';

const ERROR_COLOR  = "#ef5350";
const WARNING_COLOR = "#FDB940";

const ConnectionAlert = ({
  onRetry,
  onClose,
  connectionDetails,
  position = 'top',
  alertType = 'error',
  responseTime = null,
}) => {
  const [dismissed, setDismissed] = React.useState(false);

  const handleClose = () => {
    setDismissed(true);
    if (onClose) onClose();
  };

  const handleRetry = () => {
    setDismissed(false);
    if (onRetry) onRetry();
  };

  if (dismissed) return null;

  const isWarning = alertType === 'warning';
  const accent = isWarning ? WARNING_COLOR : ERROR_COLOR;
  const Icon = isWarning ? SignalWifiOffIcon : WifiOffIcon;

  const title = isWarning ? 'Slow connection' : 'Connection lost';

  const message = isWarning
    ? responseTime
      ? `API response taking ${responseTime}ms — operations may be slow.`
      : 'Slow connection detected — operations may take longer than usual.'
    : !connectionDetails || !connectionDetails.networkStatus
      ? 'No network detected. Check your WiFi or mobile data.'
      : !connectionDetails.internetConnection
      ? 'No internet access. Check your network connection.'
      : 'Server unreachable. Check your connection and retry.';

  const positionSx = position === 'top'
    ? { top: 0, borderBottom: `1px solid ${accent}30` }
    : { bottom: 0, borderTop: `1px solid ${accent}30` };

  return (
    <Box sx={{
      position: 'fixed',
      left: 0,
      right: 0,
      zIndex: 1400,
      ...positionSx,
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      px: '14px',
      py: '10px',
      background: `rgba(13,14,20,0.97)`,
      backdropFilter: 'blur(12px)',
      borderLeft: `3px solid ${accent}`,
    }}>
      {/* Icon */}
      <Icon sx={{ color: accent, fontSize: '1.1rem', flexShrink: 0 }} />

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
          {title}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', lineHeight: 1.3, mt: '1px' }}>
          {message}
        </Typography>
      </Box>

      {/* Retry */}
      <IconButton
        size="small"
        onClick={handleRetry}
        sx={{
          color: accent,
          backgroundColor: `${accent}14`,
          borderRadius: '6px',
          p: '5px',
          flexShrink: 0,
          '&:hover': { backgroundColor: `${accent}25` },
        }}
        title="Retry"
      >
        <RefreshIcon sx={{ fontSize: '1rem' }} />
      </IconButton>

      {/* Dismiss */}
      <IconButton
        size="small"
        onClick={handleClose}
        sx={{
          color: 'rgba(255,255,255,0.35)',
          borderRadius: '6px',
          p: '5px',
          flexShrink: 0,
          '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.08)' },
        }}
        title="Dismiss"
      >
        <CloseIcon sx={{ fontSize: '1rem' }} />
      </IconButton>
    </Box>
  );
};

export default ConnectionAlert;
